// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationsCards.jsx

import React from "react";

import {
  FaCalendarAlt,
  FaStar,
  FaUser,
} from "react-icons/fa";

import AssociationActions from "./AssociationActions";

import InlineStatutSelect from "../ui/InlineStatutSelect";

import {
  formatAssociationDate,
  getReminderState,
  splitDisciplines,
} from "../utils/association.utils";

const fallbackLogo = "/images/no-image.jpg";

export function AssociationsCards({
  data = [],
  deletingId = null,
  updatingStatusIds = [],
  onEdit,
  onDelete,
  onQuickView,
  onStatusChange,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 p-3 lg:grid-cols-2 2xl:grid-cols-3">
      {data.map((association) => {
        const disciplines = splitDisciplines(
          association.discipline_asso,
        );

        const reminder = getReminderState(
          association.date_relance_asso,
        );

        const statusUpdating =
          updatingStatusIds.includes(
            association.id,
          );

        return (
          <article
            key={association.id}
            className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500" />

            <div className="flex flex-1 flex-col p-4">
              <div className="flex items-start gap-3">
                <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 dark:border-slate-700">
                  <img
                    src={
                      association.logo_asso ||
                      fallbackLogo
                    }
                    alt=""
                    className="h-full w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src =
                        fallbackLogo;
                    }}
                  />

                  {association.is_favorite ? (
                    <span className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-[9px] text-white shadow">
                      <FaStar />
                    </span>
                  ) : null}
                </div>

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() =>
                      onQuickView?.(association)
                    }
                    className="line-clamp-2 text-left font-semibold leading-5 text-slate-900 transition hover:text-violet-600 dark:text-white dark:hover:text-violet-300"
                  >
                    {association.nom_asso || "—"}
                  </button>

                  <div className="mt-1 text-[11px] text-slate-400">
                    Dossier nº {association.id}
                  </div>

                  <div className="mt-2 inline-flex rounded-lg bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                    {association.produit_asso || "—"}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <InlineStatutSelect
                  value={
                    association.statut_asso || ""
                  }
                  size="sm"
                  updating={statusUpdating}
                  disabled={statusUpdating}
                  label={`Modifier le statut de ${association.nom_asso}`}
                  onChange={(newStatus) =>
                    onStatusChange?.(
                      association,
                      newStatus,
                    )
                  }
                  className="w-full"
                />
              </div>

              <div className="mt-4 flex min-h-6 flex-wrap gap-1">
                {disciplines.length ? (
                  disciplines.map((discipline) => (
                    <span
                      key={discipline}
                      className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                    >
                      {discipline}
                    </span>
                  ))
                ) : (
                  <span className="text-xs italic text-slate-400">
                    Aucune discipline renseignée
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/50">
                  <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    <FaCalendarAlt />
                    Prochaine relance
                  </div>

                  <div className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {formatAssociationDate(
                      association.date_relance_asso,
                    )}
                  </div>

                  {association.date_relance_asso ? (
                    <span
                      className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${reminder.tone}`}
                    >
                      {reminder.label}
                    </span>
                  ) : null}
                </div>

                <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/50">
                  <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    <FaUser />
                    Contacts
                  </div>

                  <div className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {association.prospects?.length || 0} contact
                    {association.prospects?.length > 1
                      ? "s"
                      : ""}
                  </div>

                  {association.prospects?.length ? (
                    <div className="mt-1 line-clamp-1 text-[11px] text-slate-500">
                      {association.prospects
                        .slice(0, 2)
                        .map(
                          (prospect) =>
                            `${prospect.prenom || ""} ${
                              prospect.nom || ""
                            }`,
                        )
                        .join(", ")}
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] italic text-slate-400">
                      Aucun contact associé
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <AssociationActions
                  association={association}
                  deleting={
                    deletingId === association.id
                  }
                  onQuickView={onQuickView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default AssociationsCards;